import config from '../config.js';
import { trackException, trackMetric } from '../services/applicationInsights';
import { getAccessToken } from '../auth/tokenStorage';
import { emitSessionExpiredEvent, normalizeInactiveOrganizationError } from './adminUi';

export const API_BASE_URL = config.API_BASE_URL;

export const getHeaders = (token) => {
  const authToken = token || getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
  };
};

// Enhanced logging with telemetry
export const logApiCall = (method, endpoint, duration = 0, status = null) => {
  if (config.ENABLE_LOGGING && config.ENVIRONMENT === 'development' && process.env.NODE_ENV !== 'test') {
    console.log(`[API ${method}] ${endpoint}`);
  }

  // Track API metrics
  if (duration > 0) {
    trackMetric(`API.${method}.Duration`, duration, {
      endpoint,
      status: status?.toString()
    });
  }
};

// Enhanced API wrapper with telemetry and auto-logout on 401
export const apiRequest = async (url, options = {}) => {
  const startTime = Date.now();

  try {
    const response = await fetch(url, options);
    const duration = Date.now() - startTime;

    logApiCall(options.method || 'GET', url, duration, response.status);

    // Handle 401 Unauthorized - logout
    // TODO: Enable token refresh once OAuth backend is deployed
    if (response.status === 401) {
      emitSessionExpiredEvent();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      let message = `API Error: ${response.status}`;
      try {
        const responseText = await response.text();
        if (responseText) {
          try {
            const parsed = JSON.parse(responseText);
            if (parsed?.errors && Array.isArray(parsed.errors)) {
              message = parsed.errors.map(e => e.message || JSON.stringify(e)).join(' ');
            } else {
              message = parsed?.message || parsed?.title || responseText;
            }
          } catch {
            message = responseText;
          }
        }
      } catch {
        // Keep status-based fallback when response parsing fails.
      }

      message = normalizeInactiveOrganizationError(response.status, message, `API Error: ${response.status}`);

      const apiError = new Error(message);
      apiError.status = response.status;
      throw apiError;
    }

    return response;
  } catch (error) {
    const duration = Date.now() - startTime;

    if (error?.name !== 'AbortError') {
      trackException(error, {
        url,
        method: options.method || 'GET',
        duration
      });
    }

    throw error;
  }
};
export const PREVIEW_SESSION_CONFIG = {
  expiresInMinutes: 10,
  singleUse: false,
};

// Add a helper for building full URLs
export const buildApiUrl = (endpoint) => {
  return `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
};